import styled from "styled-components";
import { useCountUp } from "react-countup";
import React, { useEffect, useRef } from "react";
import DoubleText from "../../DoubleText";
import { COLORS } from "../../../Constants/COLOR";
import { motion, useAnimation } from "framer-motion";
import DotWithText from "../../DotWithText";
import Card from "./InformationCard";
import { AiOutlineFieldTime, AiOutlineCoffee } from "react-icons/ai";
import { BsCodeSlash } from "react-icons/bs";
import { NAVBAR_MAX_WIDTH, NAVBAR_MIN_WIDTH, NAVBAR_WIDTH } from "../../Config";
import ReactGA from "react-ga4";

const Container = styled(motion.div)`
  width: calc(100% - max(${NAVBAR_MIN_WIDTH}, min(${NAVBAR_WIDTH}, ${NAVBAR_MAX_WIDTH})));
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  flex-direction: row;
  top: 0%;
  left: 0%;
  background-color: ${COLORS["main-blue-rgba"](0.4)};
  @media screen and (orientation: portrait) {
    flex-direction: column;
    height: 90%;
    align-self: center;
  }
`;
const Rectangle = styled.div`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  display: inline-block;
  transform: translate(0, -50%);
  top: 50%;
  position: absolute;
  background-color: ${(props) => props.bgColor};
`;

const BottomRightContainer = styled.div`
  flex: 1.2;
  padding-right: 5%;
  align-items: center;
  flex-direction: column;
  display: flex;
  width: 90%;
`;
const AnimatedCard = styled(motion.div)`
  width: 100%;
  display: flex;
`;
const LeftContainer = styled.div`
  justify-content: center;
  align-items: center;
  align-content: center;
  flex: 0.4;
  padding-left: 3%;
  @media screen and (orientation: portrait) {
    flex: 0.2;
    padding: 0 3%;
  }
`;
const TitleContainer = styled.div`
  height: 8vmin;
`;
const RightContainer = styled.div`
  flex: 0.6;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  @media screen and (orientation: portrait) {
    flex: 0.8;
  }
`;
const Description = styled.p`
  color: white;
  font-family: "Puritan";
  font-size: 1.4vmax;
`;
const Quote = styled.p`
  color: ${COLORS["main-yellow"]};
  font-family: "Puritan";
  font-size: 1.2vmax;
`;
const Rocket = styled(motion.img)`
  position: absolute;
  left: 0;
  top: 50%;
  width: 12%;
  height: auto;
  cursor: ${(props) => (props.clicked ? "default" : "pointer")};
  transform: translate(-50%, -50%);
  -webkit-transform-origin-y: 5%;
  z-index: 1;
`;
const CardsContainer = styled.div`
  width: 95%;

  display: flex;
  justify-content: center;
  align-content: center;
`;
const TopRightHalf = styled(BottomRightContainer)`
  justify-content: flex-end;
  padding-bottom: 5vh;
  flex: 0.5;
`;
const Numbers = styled.div`
  font-weight: bold;
  font-size: 1.2vmax;
`;
const IconContainer = styled.div`
  position: relative;
  text-align: center;
`;
const IconProps = {
  size: "50%",
  color: COLORS["main-yellow"],
};

const IconTitle = styled.div`
  color: ${COLORS["main-yellow"]};
  font-size: 1.2vmax;
  display: inline;
`;
const popInAnimation = {
  scale: [0, 1.15, 1],
  opacity: [0, 1],
  transition: {
    duration: 0.5,
    ease: "easeInOut",
  },
};
function AboutMe(props) {
  const rocketController = useAnimation();
  const graduationCardController = useAnimation();
  const mysaCardController = useAnimation();
  const heyorcaCardController = useAnimation();
  const plusGradeCardController = useAnimation();
  const progressBar = useRef(null);
  const [clickedRocket, setClickedRocket] = React.useState(false);
  const getRocketAnimation = {
    idle: () => {
      return {
        rotateX: [0, 20, 0],
        rotateZ: [0, 5, 0, -5, 0],
        transition: {
          rotateX: { duration: 0.75, ease: "linear", delay: 1, repeat: "Infinity" },
          rotateZ: { duration: 0.75, ease: "linear", delay: 1, repeat: "Infinity" },
        },
      };
    },
    toHeyOrca: (width) => {
      return {
        x: [0, 0.2 * width],
        transition: {
          x: { duration: 0.75 },
        },
      };
    },
    toMysa: (width) => {
      return {
        x: 0.4 * width,
        transition: {
          x: { duration: 0.75, delay: 0.1 },
        },
      };
    },
    toGraduation: (width) => {
      return {
        x: 0.6 * width,
        transition: {
          x: { duration: 0.75, delay: 0.1 },
        },
      };
    },
    toPlusGrade: (width) => {
      return {
        x: 0.8 * width,
        transition: {
          x: { duration: 0.75, delay: 0.1 },
        },
      };
    },
    toStop: (width) => {
      return {
        x: width,
        transition: {
          x: { duration: 0.75, delay: 0.1 },
        },
      };
    },
    stop: () => {
      return {
        rotateX: [null, 0],
        rotateZ: [null, 0],
        transition: {
          rotateX: { duration: 0.75, ease: "linear" },
          rotateZ: { duration: 0.75, ease: "linear" },
        },
      };
    },
  };
  const initialSequence = async () => {
    await rocketController.start(getRocketAnimation.idle());
  };

  const linedOfCodeRef = React.useRef(null);
  const hoursRef = React.useRef(null);
  const cupsOfCoffeeRef = React.useRef(null);
  const { start: startHours, pauseResume: pauseResumeHours } = useCountUp({
    ref: hoursRef,
    start: 0,
    end: 16000,
    startOnMount: false,
    suffix: "+",
    duration: 22,
  });
  const { start: startCoffee, pauseResume: pauseResumeCoffee } = useCountUp({
    ref: cupsOfCoffeeRef,
    start: 0,
    end: 2000,
    suffix: "+",
    duration: 22,
    startOnMount: false,
  });
  const { start: startLines, pauseResume: pauseResumeLines } = useCountUp({
    ref: linedOfCodeRef,
    start: 0,
    end: 1000000,
    suffix: "+",
    startOnMount: false,
    duration: 22,
  });
  const startNumbers = () => {
    startLines();
    startHours();
    startCoffee();
  };
  const pauseResumeNumbers = () => {
    pauseResumeLines();
    pauseResumeCoffee();
    pauseResumeHours();
  };
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/about-me", title: "About Me" });
    initialSequence();
    startNumbers();
    pauseResumeNumbers();
  }, []);
  const onClickRocket = async () => {
    if (clickedRocket) return;
    setClickedRocket(true);
    const barWidth = progressBar.current.offsetWidth;
    startNumbers();
    await rocketController.start(getRocketAnimation.toHeyOrca(barWidth));
    pauseResumeNumbers();
    await heyorcaCardController.start(popInAnimation);
    pauseResumeNumbers();
    await rocketController.start(getRocketAnimation.toMysa(barWidth));
    pauseResumeNumbers();
    await mysaCardController.start(popInAnimation);
    pauseResumeNumbers();
    await rocketController.start(getRocketAnimation.toGraduation(barWidth));
    pauseResumeNumbers();
    await graduationCardController.start(popInAnimation);
    pauseResumeNumbers();
    await rocketController.start(getRocketAnimation.toPlusGrade(barWidth));
    pauseResumeNumbers();
    await plusGradeCardController.start(popInAnimation);
    pauseResumeNumbers();
    await rocketController.start(getRocketAnimation.toStop(barWidth));
    await rocketController.start(getRocketAnimation.stop());
  };

  const dotSize = "1.8vmax";
  const dotFontSize = "0.75vmax";
  const dotTextMargin = "3vh 0";
  return (
    <Container>
      <LeftContainer>
        <TitleContainer>
          <DoubleText
            offset={0.3}
            firstColor={COLORS["main-black"]}
            secondColor={COLORS["main-yellow"]}
            size={"3.5vmax"}
            font="Prompt"
            height="100%"
            animationProps={{
              animationColor: COLORS["main-green"],
              translateAnimation: true,
              duration: 0.3,
              scaleAnimation: true,
              absoluteDelay: 0.3,
              opacityAnimation: true,
              delayBetweenLetters: 0.05,
            }}
            text={`Me, Myself & I`}
          />
        </TitleContainer>

        <Description>
          Passionate about all things technology related. I pride my self in my work, with experience building
          sustainable, scalable and redundant systems. A heavy focus on Frontend technologies with experience in backend
          and Devops infrastructure.
          <br></br>
          <br></br>Always looking for opportunities to grow and hopefully make an impact in the industry.
          <br></br>
          <br></br>Hint: Don't forget to click the rocket!
        </Description>
        <Quote>Favorite Quote: “Programming is a social activity.” - Uncle Bob</Quote>
      </LeftContainer>
      <RightContainer>
        <TopRightHalf>
          <CardsContainer style={{ flex: 0.5 }}>
            <AnimatedCard initial={{ opacity: 0, scale: 0 }} animate={graduationCardController}>
              <Card
                title="Computer Science - Honours"
                titleFontSize="1.1vmax"
                bulletPointFontSize="0.9vmax"
                details={[
                  { fontSize: "0.8vmax", text: "Memorial University Of Newfoundland", bold: true },
                  { fontSize: "0.7vmax", text: "Sept 2016 - Apr 2021" },
                ]}
                bulletPoints={[
                  "Graduated with honours.",
                  "Wrote a research paper on utilizing evolutionary machine learning to create university course offerings schedule.",
                ]}
              />
            </AnimatedCard>
            <AnimatedCard initial={{ opacity: 0, scale: 0 }} animate={plusGradeCardController}>
              <Card
                title="Team/Technical Lead"
                titleFontSize="1.1vmax"
                bulletPointFontSize="0.9vmax"
                details={[
                  { fontSize: "0.8vmax", text: "Plusgrade", bold: true },
                  { fontSize: "0.7vmax", text: "Nov 2023 - Present" },
                ]}
                bulletPoints={[
                  "Led design of A/B experimentation system.",
                  "Architected high-scale systems for millions of users.",
                  "Drove 15% revenue increase via experimentation.",
                ]}
              />
            </AnimatedCard>
          </CardsContainer>
        </TopRightHalf>
        <BottomRightContainer>
          <div style={{ width: "90%", flex: 0.2 }}>
            <div style={{ position: "relative", width: "100%" }}>
              <Rocket
                initial={{ translateX: "-50%", translateY: "-50%" }}
                animate={rocketController}
                clicked={clickedRocket}
                onTap={onClickRocket}
                src={process.env.PUBLIC_URL + "/Images/Background/Rocket-Right.png"}
              />
              <DotWithText
                fontSize={dotFontSize}
                title="Sept 2016"
                left="0%"
                radius={dotSize}
                margin={dotTextMargin}
                bgColor={COLORS["main-yellow"]}
              />
              <DotWithText
                fontSize={dotFontSize}
                title="Heyorca - Jan 2018"
                left="20%"
                radius={dotSize}
                margin={dotTextMargin}
                bgColor={COLORS["main-yellow"]}
              />
              <DotWithText
                fontSize={dotFontSize}
                title="Mysa - Jan 2020"
                left="40%"
                radius={dotSize}
                margin={dotTextMargin}
                bgColor={COLORS["main-yellow"]}
              />
              <DotWithText
                fontSize={dotFontSize}
                title="Graduation - Apr 2021"
                left="60%"
                radius={dotSize}
                margin={dotTextMargin}
                bgColor={COLORS["main-yellow"]}
              />
              <Rectangle ref={progressBar} width="100%" height="0.5vmax" bgColor={COLORS["main-yellow"]} />
              <DotWithText
                fontSize={dotFontSize}
                title="Plusgrade - Nov 2023"
                left="80%"
                radius={dotSize}
                margin={dotTextMargin}
                bgColor={COLORS["main-yellow"]}
              />
              <DotWithText
                fontSize={dotFontSize}
                title="Present"
                left="100%"
                radius={dotSize}
                margin={dotTextMargin}
                bgColor={COLORS["main-yellow"]}
              />
            </div>
          </div>
          <CardsContainer style={{ flex: 0.6 }}>
            <AnimatedCard initial={{ opacity: 0, scale: 0 }} animate={heyorcaCardController}>
              <Card
                title="Web Developer"
                titleFontSize="1.1vmax"
                bulletPointFontSize="0.9vmax"
                details={[
                  { fontSize: "0.8vmax", text: "Heyorca", bold: true },
                  { fontSize: "0.7vmax", text: "Jan 2018 - Oct 2019" },
                ]}
                bulletPoints={[
                  "Develop and maintain Laravel PHP app.",
                  "Develop and maintain a serverless AWS infrastructure.",
                  "Worked as a member on the devops team to create internal tooling.",
                ]}
              />
            </AnimatedCard>
            <AnimatedCard initial={{ opacity: 0, scale: 0 }} animate={mysaCardController}>
              <Card
                title="App Architecture Lead"
                titleFontSize="1.1vmax"
                bulletPointFontSize="0.9vmax"
                details={[
                  { fontSize: "0.8vmax", text: "Devops Team Lead", bold: true },
                  { fontSize: "0.8vmax", text: "Mysa Thermostat", bold: true },
                  { fontSize: "0.7vmax", text: "Jan 2020 - Present" },
                ]}
                bulletPoints={[
                  "Technical Lead responsible over React Native app architecture.",
                  "Lead devops team.",
                  "Selected Employee of the week multiple times.",
                ]}
              />
            </AnimatedCard>
          </CardsContainer>
          <div style={{ width: "90%", flex: 0.3 }}>
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "5%" }}>
              <IconContainer>
                <BsCodeSlash {...IconProps} />
                <IconTitle>
                  <Numbers ref={linedOfCodeRef}>0</Numbers> Lines Of Code
                </IconTitle>
              </IconContainer>
              <IconContainer>
                <AiOutlineFieldTime {...IconProps} />
                <IconTitle>
                  <Numbers ref={hoursRef}>0</Numbers> Professional Work Hours
                </IconTitle>
              </IconContainer>
              <IconContainer>
                <AiOutlineCoffee {...IconProps} />
                <IconTitle>
                  <Numbers ref={cupsOfCoffeeRef}>0</Numbers> Cups of Coffee
                </IconTitle>
              </IconContainer>
            </div>
          </div>
        </BottomRightContainer>
      </RightContainer>
    </Container>
  );
}

export default AboutMe;
